// A tiny fetch-into-state hook for the library's tab payloads.
//
// Deliberately not react-query: that is a new dependency against a codebase with a
// consistent hand-rolled fetch style, for four payloads that need almost none of it.
//
// Two behaviours here are load-bearing rather than conveniences:
//
//   * `data` is KEPT while refetching. Flipping a Browse-all filter or a chart metric
//     must not blank the section and collapse the page height under the reader's cursor.
//   * `isFeatureUnavailable` is treated as EMPTY, not as an error. That is what lets the
//     backend land tab by tab: a 404/501 on a not-yet-built endpoint renders the section's
//     honest empty state instead of a red error strip.

import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "./api";
import type { SectionState } from "./library-types";

/** A 404/405/501 from an endpoint that simply is not built yet. */
function isFeatureUnavailable(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 404 || error.status === 405 || error.status === 501);
}

export interface AsyncResult<T> {
  data: T | null;
  state: SectionState;
  error: string | null;
  reload: () => void;
}

/**
 * Fetch `load()` whenever `deps` change.
 *
 * `load` is called with an AbortSignal-free contract for simplicity; instead a generation
 * counter discards stale responses, which also covers the React-Router case where the same
 * component is reused across two URLs.
 */
export function useAsync<T>(
  load: () => Promise<T>,
  deps: unknown[],
  { enabled = true }: { enabled?: boolean } = {},
): AsyncResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [state, setState] = useState<SectionState>(enabled ? "loading" : "empty");
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const generation = useRef(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) {
      setState("empty");
      return;
    }
    const mine = ++generation.current;
    // Only show the skeleton on a FIRST load. A refetch keeps the previous data on screen.
    setState((current) => (current === "ready" ? "ready" : "loading"));
    setError(null);

    load()
      .then((result) => {
        if (generation.current !== mine) return;
        setData(result);
        setState("ready");
      })
      .catch((e: unknown) => {
        if (generation.current !== mine) return;
        if (isFeatureUnavailable(e)) {
          // Not built yet — the section shows its empty state, not an error.
          setState("empty");
          return;
        }
        setError(e instanceof ApiError ? e.detail : "Something went wrong.");
        setState("error");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, enabled, nonce]);

  return { data, state, error, reload };
}
